import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, Navigate, useLocation } from "react-router-dom";
import axios from "axios";
import { Alertmessage } from "../../widgets";
import {
  FaBars,
  FaCheckCircle,
  FaChevronDown,
  FaTimes,
  FaUsers,
  FaSync,
  FaExclamationCircle,
  FaClipboardCheck,
  FaMoneyBillWave,
  FaFileInvoiceDollar,
  FaCalendarAlt,
} from "react-icons/fa";
import "./Pjc_dashboard.css";
// Reuse the beautiful layout styling of the PJC system

axios.defaults.withCredentials = true;

const ADMIN_NAVIGATION = [
  { label: "Pending Approvals", icon: FaClipboardCheck },
  { label: "All Transactions", icon: FaFileInvoiceDollar },
  { label: "Alumni / Users", icon: FaUsers },
  //{ label: "Payment Plans", icon: FaFileInvoiceDollar },
  { label: "Alumni Payment Entry", icon: FaMoneyBillWave },
];

const STATUS_FILTER_OPTIONS = ["All", "Pending", "Approved", "Rejected"];

const formatMoney = (value) =>
  new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 0,
  }).format(value || 0);

const formatDate = (value) => {
  if (!value) return "-";
  const d = new Date(value);
  if (isNaN(d.getTime())) return value;
  return d.toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

// Converts date to yyyy-mm-dd using local timezone
const toDateKey = (value) => {
  if (!value) return null;
  const d = new Date(value);
  if (isNaN(d.getTime())) return null;
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const AdminDashboard = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const [authStatus, setAuthStatus] = useState("loading");
  const [currentUser, setCurrentUser] = useState(null);

  // UI Navigation State
  const [mobileOpen, setMobileOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [activeItem, setActiveItem] = useState("Pending Approvals");
  const [alertMsg, setAlertMsg] = useState("");
  const [rejectPrompt, setRejectPrompt] = useState(null);
  const [confirmModal, setConfirmModal] = useState(null);

  // Data Loading & Error States
  const [loadingData, setLoadingData] = useState(true);
  const [dataError, setDataError] = useState("");
  const [pendingApprovals, setPendingApprovals] = useState([]);
  const [allTransactions, setAllTransactions] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");

  // Filter State (All Transactions panel)
  const [statusFilter, setStatusFilter] = useState("All");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  const [registeredAlumni, setRegisteredAlumni] = useState([]);
  const [totalPledged, setTotalPledged] = useState(0);

  const isSuperAdmin = currentUser?.role?.toLowerCase() === "superadmin";
  useEffect(() => {
    let isMounted = true;

    const verifySession = async () => {
      try {
        const res = await axios.get("/backend/admin/login_verification");
        if (isMounted && res.data && res.data.token_status === "okay") {
          setCurrentUser(res.data.user || null);
          setAuthStatus("authenticated");
          return;
        }
      } catch (err) {
        console.log("Session verification failed:", err?.message);
      }

      if (isMounted) {
        setAuthStatus("unauthenticated");
      }
    };

    verifySession();

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    if (location.state?.activeItem) {
      setActiveItem(location.state.activeItem);
    }
  }, [location.state]);

  useEffect(() => {
    if (authStatus !== "authenticated") return;
    fetchAdminData();
  }, [authStatus]);

  const fetchAdminData = async () => {
    setLoadingData(true);
    setDataError("");
    try {
      const [pendingRes, approvedRes, rejectedRes, alumniRes, dashboardRes] = await Promise.allSettled([
        axios.get("/backend/admin/transactions/pending"),
        axios.post("/backend/admin/transactions/approved"),
        axios.post("/backend/admin/transactions/rejected"),
        axios.get("/backend/admin/alumni"),
        axios.get("/backend/admin/dashboard-stats")

      ]);

      let pending = [];
      if (pendingRes.status === "fulfilled" && pendingRes.value.data?.pending) {
        pending = pendingRes.value.data.pending;
        setPendingApprovals(pending);
      } else {
        setPendingApprovals([]);
      }

      let approved = [];
      if (approvedRes.status === "fulfilled" && approvedRes.value.data?.approved) {
        approved = approvedRes.value.data.approved;
      }

      let rejected = [];
      if (rejectedRes.status === "fulfilled" && rejectedRes.value.data?.rejected) {
        rejected = rejectedRes.value.data.rejected;
      }

      const normalizeTransaction = (t, status) => ({
        ...t,

        approval_status: status,

        reference: t.reference || t.transaction_reference,
        amount: Number(t.amount || t.amount_paid || 0),
        method: t.method || t.payment_mode,
        date: t.date || t.payment_date,

        source: t.source || "transactions",
      });

      const combined = [
        ...pending.map((t) => normalizeTransaction(t, "PENDING")),
        ...approved.map((t) => normalizeTransaction(t, "APPROVED")),
        ...rejected.map((t) => normalizeTransaction(t, "REJECTED")),
      ];
      setAllTransactions(combined);
      //console.log("Combined Transactions:", combined);

      if (alumniRes.status === "fulfilled" && alumniRes.value.data?.alumni) {
        setRegisteredAlumni(alumniRes.value.data.alumni);
      } else {
        setRegisteredAlumni([]);
      }
      if (
        dashboardRes.status === "fulfilled" &&
        dashboardRes.value.data.success
      ) {
        setTotalPledged(dashboardRes.value.data.totalPledged);
      } else {
        setTotalPledged(0);
      }
    } catch (err) {
      console.error("Error loading admin records:", err);
      setDataError("Failed to fetch administrative records.");
    } finally {
      setLoadingData(false);
    }
  };

  const handleLogout = async () => {
    try {
      await axios.post("/backend/admin/logout", {});
    } catch { }
    setAuthStatus("unauthenticated");
    navigate("/alumni-login", { replace: true, state: { loggedOut: true } });
  };

  const handleAdminApprove = (item) => {
    setConfirmModal({
      message: "Are you sure you want to approve this transaction?",
      onConfirm: async () => {
        setConfirmModal(null);
        try {
          await axios.post(`/backend/admin/transactions/${item.id}/approve`);
          setAlertMsg(`Transaction ${item.transaction_reference || item.reference || ""} approved successfully!`);
          fetchAdminData();
        } catch (err) {
          console.error("Approve failed:", err);
          setAlertMsg(err.response?.data?.error || "Failed to approve transaction.");
        }
      },
    });
  };

  const openRejectPrompt = (item) => {
    setRejectPrompt({
      item,
      reasonValue: "",
    });
  };

  const handleAdminReject = async () => {
    if (!rejectPrompt) return;
    const { item, reasonValue } = rejectPrompt;
    if (!reasonValue || !reasonValue.trim()) return;

    setRejectPrompt(null);
    try {
      await axios.post(`/backend/admin/transactions/${item.id}/reject`, { reason: reasonValue.trim() });
      setAlertMsg(`Transaction ${item.transaction_reference || item.reference || ""} rejected.`);
      fetchAdminData();
    } catch (err) {
      console.error("Reject failed:", err);
      setAlertMsg(err.response?.data?.error || "Failed to reject transaction.");
    }
  };

  // Applies ONLY the date-range filter (not status / search) on top of allTransactions.
  // This is the source used to power the 3 summary cards, so the cards always show a
  // full Pending / Approved / Total breakdown for whatever date range is selected,
  // independent of which status the table itself is currently filtered to.
  const dateFilteredTransactions = useMemo(() => {
    let result = allTransactions;

    if (startDate) {
      result = result.filter((t) => {
        const txDate = toDateKey(t.date);
        if (!txDate) return false;

        if (!endDate) {
          return txDate >= startDate;
        }

        return txDate >= startDate && txDate <= endDate;
      });
    } else if (endDate) {
      result = result.filter((t) => {
        const txDate = toDateKey(t.payment_date || t.date);
        return txDate && txDate <= endDate;
      });
    }

    return result;
  }, [allTransactions, startDate, endDate]);

  // Summary card metrics — recalculated live from dateFilteredTransactions.
  const summaryPendingCount = useMemo(
    () =>
      dateFilteredTransactions.filter(
        (t) => (t.approval_status || "").toUpperCase() === "PENDING"
      ).length,
    [dateFilteredTransactions]
  );

  const summaryApprovedCount = useMemo(
    () =>
      dateFilteredTransactions.filter(
        (t) => (t.approval_status || "").toUpperCase() === "APPROVED"
      ).length,
    [dateFilteredTransactions]
  );

  const summaryApprovedTotal = useMemo(
    () =>
      dateFilteredTransactions
        .filter((t) => (t.approval_status || "").toUpperCase() === "APPROVED")
        .reduce((sum, t) => sum + Number(t.amount || t.amount_paid || 0), 0),
    [dateFilteredTransactions]
  );

  const filteredAllTransactions = useMemo(() => {
    let result = allTransactions;
    // console.log("Status Filter:", statusFilter);
    // console.log("Start Date:", startDate);
    // console.log("End Date:", endDate);

    // console.table(
    //   allTransactions.map(t => ({
    //     id: t.id,
    //     status: t.approval_status,
    //     date: t.payment_date,
    //     source: t.source
    //   }))
    // );
    // Search filter (name, reference, batch, payment method)
    if (searchQuery.trim()) {
      const q = searchQuery.trim().toLowerCase();
      result = result.filter(
        (t) =>
          (t.fullname || t.donor_name || "").toLowerCase().includes(q) ||
          (t.reference || t.transaction_reference || "").toLowerCase().includes(q) ||
          (t.batch && t.batch.toString().toLowerCase().includes(q)) ||
          (t.payment_mode || t.method || "").toLowerCase().includes(q)
      );
    }

    // Status filter
    if (statusFilter !== "All") {
      result = result.filter(
        (t) => (t.approval_status || "PENDING").toUpperCase() === statusFilter.toUpperCase()
      );
    }

    // Date range filter
    if (startDate) {
      result = result.filter((t) => {
        const txDate = toDateKey(t.payment_date || t.date);
        if (!txDate) return false;

        if (!endDate) {
          return txDate >= startDate;
        }

        return txDate >= startDate && txDate <= endDate;
      });
    } else if (endDate) {
      result = result.filter((t) => {
        const txDate = toDateKey(t.payment_date || t.date);
        return txDate && txDate <= endDate;
      });
    }

    result = [...result].sort((a, b) => {
      const dateA = new Date(a.payment_date || a.date || 0);
      const dateB = new Date(b.payment_date || b.date || 0);
      return dateB - dateA;
    });

    return result;
  }, [allTransactions, searchQuery, statusFilter, startDate, endDate]);

  const clearDateFilters = () => {
    setStartDate("");
    setEndDate("");
  };

  const clearAllFilters = () => {
    setSearchQuery("");
    setStatusFilter("All");
    setStartDate("");
    setEndDate("");
  };

  if (authStatus === "loading") {
    return (
      <div className="pjc-loading-screen">
        <div className="pjc-spinner" />
        <p>Loading Admin Portal...</p>
      </div>
    );
  }

  if (authStatus === "unauthenticated") {
    return <Navigate to="/alumni-login" replace />;
  }

  return (
    <div className="pjc-dashboard-page">
      {mobileOpen && (
        <button
          className="pjc-sidebar-backdrop"
          onClick={() => setMobileOpen(false)}
          aria-label="Close navigation sidebar"
        />
      )}

      <aside className={`pjc-sidebar ${sidebarCollapsed ? "is-collapsed" : ""} ${mobileOpen ? "is-open" : ""}`}>
        <div className="pjc-brand">
          <div className="pjc-brand-mark">P</div>
          <div className="pjc-brand-text">
            <span>PJC Fund</span>
            <small>Admin View</small>
          </div>
          <button className="pjc-close-sidebar" onClick={() => setMobileOpen(false)} aria-label="Close navigation">
            <FaTimes />
          </button>
        </div>

        <p className="pjc-sidebar-label">MANAGEMENT</p>
        <nav className="pjc-nav" aria-label="Dashboard navigation">
          {ADMIN_NAVIGATION.filter(
            (item) => item.label !== "Alumni Payment Entry" || isSuperAdmin
          ).map(({ label, icon: Icon }) => (
            <button
              key={label}
              className={`pjc-nav-link ${activeItem === label ? "active" : ""}`}
              onClick={() => {
                if (label === "Alumni Payment Entry") {
                  if (!isSuperAdmin) {
                    setAlertMsg("Only Super Admin can access Alumni Payment Entry.");
                    return;
                  }

                  navigate("/payment-entry");
                } else {
                  setActiveItem(label);
                }

                setMobileOpen(false);
              }}
              title={label}
            >
              <Icon aria-hidden="true" />
              <span>{label}</span>
            </button>
          ))}
        </nav>

        {/* <button
          className="pjc-collapse-button"
          onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
          aria-label="Toggle sidebar width"
        >
          {sidebarCollapsed ? <FaChevronRight /> : <FaChevronLeft />}
          <span>Collapse Sidebar</span>
        </button> */}
      </aside>


      <main className="pjc-dashboard-content">


        <header className="pjc-topbar">

          <div className="pjc-topbar-title-group">

            <button className="pjc-menu-button" onClick={() => setMobileOpen(true)} aria-label="Open sidebar menu">
              <FaBars />
            </button>
            <div className="pjc-brand-logo-wrapper">
              <img
                src="https://accet-site-media-trial.s3.ap-northeast-1.amazonaws.com/Pjc+module/pjc+logo+(1).webp"
                alt="PJC Fund Logo"
                className="pjc-brand-logo"
              />
            </div>
            <div>
              <p className="pjc-eyebrow">ACGCET ALUMNI FUND SYSTEM</p>
              <h1>Admin Portal</h1>
            </div>
          </div>

          <div className="pjc-topbar-actions">
            <div className="pjc-profile-badge">
              <span className="pjc-avatar">
                {currentUser?.username ? currentUser.username.substring(0, 2).toUpperCase() : "AD"}
              </span>
              <div className="pjc-profile-meta">
                <strong>{currentUser?.username || "Administrator"}</strong>
                <small>PJC Administrator</small>
              </div>
            </div>

            <button className="pjc-action-btn secondary" onClick={handleLogout} title="Logout of session">
              Logout
            </button>
          </div>
        </header>

        <div className="pjc-dashboard-body">
          {dataError && (
            <div className="pjc-alert warning">
              <FaExclamationCircle /> {dataError}
            </div>
          )}

          <section className="pjc-dashboard-section">
            <div className="pjc-summary-grid pjc-admin-summary-grid">
              <article className="pjc-summary-card">
                <div className="pjc-summary-icon orange">
                  <FaClipboardCheck />
                </div>
                <div>
                  <p>Pending Approvals</p>
                  <h3>{summaryPendingCount}</h3>
                  <small>Requires Verification</small>
                </div>
              </article>

              <article className="pjc-summary-card">
                <div className="pjc-summary-icon green">
                  <FaCheckCircle />
                </div>
                <div>
                  <p>Approved Transactions</p>
                  <h3>{summaryApprovedCount}</h3>
                  <small>Verified Remittances</small>
                </div>
              </article>

              <article className="pjc-summary-card">
                <div className="pjc-summary-icon blue">
                  <FaMoneyBillWave />
                </div>
                <div>
                  <p>Total Fund Collections</p>
                  <h3>{formatMoney(summaryApprovedTotal)}</h3>
                  <small>Approved Alumni Funds</small>
                </div>
              </article>

              <article className="pjc-summary-card">
                <div className="pjc-summary-icon blue">
                  <FaMoneyBillWave />
                </div>
                <div>
                  <p>Total Pledges</p>
                  <h3>{formatMoney(totalPledged)}</h3>
                  <small>Total Pledged Amount</small>
                </div>
              </article>
            </div>

            {/* Pending Approvals Table */}
            {activeItem === "Pending Approvals" && isSuperAdmin && (
              <section className="pjc-panel">
                <div className="pjc-panel-heading">
                  <div>
                    <h2>Pending Payment Verification</h2>
                    <p>Verify submitted payment receipts. Approving will credit the corresponding installment.</p>
                  </div>
                  <button className="pjc-icon-button" onClick={fetchAdminData} title="Refresh Records">
                    <FaSync />
                  </button>
                </div>
                <div className="pjc-table-wrap">
                  <table>
                    <thead>
                      <tr>
                        <th>Alumni Name</th>
                        <th>Batch</th>
                        <th>Reference</th>
                        <th>Amount</th>
                        <th>Method</th>
                        <th>Date</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {loadingData ? (
                        <tr>
                          <td colSpan={7} className="pjc-empty-cell">
                            Loading pending approvals...
                          </td>
                        </tr>
                      ) : pendingApprovals.length === 0 ? (
                        <tr>
                          <td colSpan={7} className="pjc-empty-cell">
                            No pending payment approvals right now. All submissions are verified!
                          </td>
                        </tr>
                      ) : (
                        pendingApprovals.map((item) => (
                          <tr key={item.id}>
                            <td data-label="Alumni Name">
                              <strong>{item.fullname || item.donor_name || "Alumni User"}</strong>
                            </td>
                            <td data-label="Batch">{item.batch || "N/A"}</td>
                            <td data-label="Reference" className="pjc-reference">
                              {item.transaction_reference || item.reference || `TXN-${item.id}`}
                            </td>
                            <td data-label="Amount">
                              <strong>{formatMoney(item.amount || item.amount_paid)}</strong>
                            </td>
                            <td data-label="Method">{item.payment_mode || item.method}</td>
                            <td data-label="Date">{formatDate(item.payment_date || item.date)}</td>
                            <td data-label="Actions">
                              <div style={{ display: "flex", gap: "6px", flexWrap: "wrap" }}>
                                <button
                                  className="pjc-action-btn approve"
                                  type="button"
                                  onClick={() => handleAdminApprove(item)}
                                >
                                  Approve
                                </button>
                                <button
                                  className="pjc-action-btn reject"
                                  type="button"
                                  onClick={() => openRejectPrompt(item)}
                                >
                                  Reject
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </section>
            )}

            {activeItem === "All Transactions" && (
              <section className="pjc-panel">
                <div className="pjc-panel-heading">
                  <div>
                    <h2>All Transactions</h2>
                    <p>Complete historical log of alumni payments across all batches.</p>
                  </div>
                  <button className="pjc-icon-button" onClick={fetchAdminData} title="Refresh Records">
                    <FaSync />
                  </button>
                </div>

                {/* Filter Bar: search, status, date range */}
                <div className="pjc-filter-toolbar">
                  {/* <div className="pjc-search-bar">
                    <FaSearch />
                    <input
                      type="text"
                      placeholder="Search by alumni name, reference, batch..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                    />
                  </div> */}

                  <div className="pjc-filter-group">
                    <div className="pjc-select-wrap">
                      <select
                        className="pjc-filter-select"
                        aria-label="Filter by status"
                        value={statusFilter}
                        onChange={(e) => setStatusFilter(e.target.value)}
                      >
                        {STATUS_FILTER_OPTIONS.map((opt) => (
                          <option key={opt} value={opt}>
                            {opt === "All" ? "All Statuses" : opt}
                          </option>
                        ))}
                      </select>
                      <FaChevronDown className="pjc-select-caret" aria-hidden="true" />
                    </div>

                    <div className="pjc-date-field">
                      <label className="pjc-date-field-label" htmlFor="pjc-start-date">
                        Start Date
                      </label>
                      <div className={`pjc-date-filter-wrap ${startDate ? "has-value" : ""}`}>
                        <FaCalendarAlt className="pjc-date-filter-icon" aria-hidden="true" />
                        <input
                          id="pjc-start-date"
                          type="date"
                          className="pjc-filter-date"
                          aria-label="Start date"
                          value={startDate}
                          max={endDate || undefined}
                          onChange={(e) => setStartDate(e.target.value)}
                        />
                        {startDate && (
                          <button
                            type="button"
                            className="pjc-date-clear-btn"
                            onClick={() => setStartDate("")}
                            aria-label="Clear start date"
                          >
                            <FaTimes />
                          </button>
                        )}
                      </div>
                    </div>

                    <div className="pjc-date-field">
                      <label className="pjc-date-field-label" htmlFor="pjc-end-date">
                        End Date
                      </label>
                      <div className={`pjc-date-filter-wrap ${endDate ? "has-value" : ""}`}>
                        <FaCalendarAlt className="pjc-date-filter-icon" aria-hidden="true" />
                        <input
                          id="pjc-end-date"
                          type="date"
                          className="pjc-filter-date"
                          aria-label="End date"
                          value={endDate}
                          min={startDate || undefined}
                          onChange={(e) => setEndDate(e.target.value)}
                        />
                        {endDate && (
                          <button
                            type="button"
                            className="pjc-date-clear-btn"
                            onClick={() => setEndDate("")}
                            aria-label="Clear end date"
                          >
                            <FaTimes />
                          </button>
                        )}
                      </div>
                    </div>

                    {(searchQuery || statusFilter !== "All" || startDate || endDate) && (
                      <button type="button" className="pjc-clear-date-btn" onClick={clearAllFilters}>
                        <FaTimes /> Clear Filters
                      </button>
                    )}
                  </div>
                </div>

                {(startDate || endDate) && (
                  <div className="pjc-active-filter-bar">
                    Showing transactions from
                    {startDate ? <strong> {formatDate(startDate)} </strong> : " the beginning "}
                    {endDate ? "to " : " to present"}
                    {endDate && <strong> {formatDate(endDate)}</strong>}
                    <button type="button" onClick={clearDateFilters}>
                      Clear <FaTimes />
                    </button>
                  </div>
                )}

                <div className="pjc-table-wrap">
                  <table className="pjc-responsive-table">
                    <thead>
                      <tr>
                        <th>Alumni Name</th>
                        <th>Batch</th>
                        <th>Reference </th>
                        <th>Inst</th>
                        <th>Amount</th>
                        <th>Method</th>
                        <th>Date</th>
                        <th>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {loadingData ? (
                        <tr>
                          <td colSpan={8} className="pjc-empty-cell">
                            Loading all transactions...
                          </td>
                        </tr>
                      ) : filteredAllTransactions.length === 0 ? (
                        <tr>
                          <td colSpan={8} className="pjc-empty-cell">
                            {searchQuery || statusFilter !== "All" || startDate || endDate
                              ? "No transactions found for the selected filters."
                              : "No transaction records found."}
                          </td>
                        </tr>
                      ) : (
                        filteredAllTransactions.map((tx) => (
                          <tr key={`${tx.source}-${tx.id}`}>
                            <td data-label="Alumni Name">
                              <strong>{tx.fullname || tx.donor_name || "Alumni"}</strong>
                            </td>
                            <td data-label="Batch">{tx.batch || "-"}</td>
                            <td data-label="Reference" className="pjc-reference">
                              {tx.reference || `TXN-${tx.id}`}
                            </td>
                            <td data-label="Inst">#{tx.installment_no || "-"}</td>
                            <td data-label="Amount">{formatMoney(tx.amount)}</td>
                            <td data-label="Method">{tx.method}</td>
                            <td data-label="Date">{formatDate(tx.date)}</td>
                            <td data-label="Status">
                              <span className={`pjc-status ${(tx.approval_status || "pending").toLowerCase().replace(/\s+/g, "-")}`}>
                                {tx.approval_status || "PENDING"}
                              </span>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </section>
            )}

            {activeItem === "Alumni / Users" && (
              <section className="pjc-panel">
                <div className="pjc-panel-heading">
                  <div>
                    <h2>Registered Alumni</h2>
                    <p>Overview of alumni members and their pledged contributions.</p>
                  </div>
                </div>
                <div className="pjc-table-wrap">
                  <table className="pjc-responsive-table">
                    <thead>
                      <tr>
                        <th>Alumni Name</th>
                        <th>Batch / Branch</th>
                        <th>Email</th>
                        <th>Phone</th>
                        <th>Fund Type</th>
                        <th>Total Pledged</th>
                        <th>Total Paid</th>
                      </tr>
                    </thead>
                    <tbody>
                      {loadingData ? (
                        <tr>
                          <td colSpan={7} className="pjc-empty-cell">
                            Loading registered alumni directory...
                          </td>
                        </tr>
                      ) : registeredAlumni.length === 0 ? (
                        <tr>
                          <td colSpan={7} className="pjc-empty-cell">
                            No registered alumni records found.
                          </td>
                        </tr>
                      ) : (
                        registeredAlumni.map((usr) => (
                          <tr key={usr.id}>
                            <td data-label="Alumni Name">
                              <strong>{usr.fullname}</strong>
                            </td>
                            <td data-label="Batch / Branch">
                              {usr.batch ? `${usr.batch} / ${usr.branch || ""}` : "Guest"}
                            </td>
                            <td data-label="Email">{usr.email}</td>
                            <td data-label="Phone">{usr.phone || "-"}</td>
                            <td data-label="Fund Type">{usr.fund_type || "N/A"}</td>
                            <td data-label="Total Pledged">{formatMoney(usr.total_amount || 0)}</td>
                            <td data-label="Total Paid">{formatMoney(usr.total_paid || 0)}</td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </section>
            )}

            {activeItem === "Payment Plans" && (
              <section className="pjc-panel pjc-details-panel">
                <div className="pjc-panel-heading">
                  <div>
                    <h2>Payment Plans System</h2>
                    <p>Structured payment plans synced live with user pledges and transactions.</p>
                  </div>
                </div>
                <div style={{ padding: "24px", color: "#62748e", fontSize: "14px" }}>
                  All Contribution schedules and payment plans are generated dynamically upon user registration. Select{" "}
                  <strong>Pending Approvals</strong> or <strong>All Transactions</strong> to process alumni payments.
                </div>
              </section>
            )}
          </section>
        </div>
      </main>
      <Alertmessage message={alertMsg} onClose={() => setAlertMsg("")} />
      <Alertmessage
        type="confirm"
        message={confirmModal?.message}
        onConfirm={confirmModal?.onConfirm}
        onCancel={() => setConfirmModal(null)}
      />
      <Alertmessage
        type="prompt"
        message={rejectPrompt ? "Enter rejection reason for Alumni:" : ""}
        inputValue={rejectPrompt?.reasonValue || ""}
        onInputChange={(e) => setRejectPrompt(prev => ({ ...prev, reasonValue: e.target.value }))}
        onConfirm={handleAdminReject}
        onCancel={() => setRejectPrompt(null)}
      />
    </div>
  );
};

export default AdminDashboard;